import React, { useEffect, useState } from 'react';
import QueryString from 'qs';
import { Button, ButtonDropdown, Card, ErrorMessage } from 'ui-toolkit';
import { RouteComponentProps, useHistory } from 'react-router';
import { useSelector } from 'react-redux';
import { compose } from '@reduxjs/toolkit';
import { convertPaginationQueryStringToPaginationQuery } from '../../types/rest/IPaginationQuery';
import { convertSortQueryStringToSortQuery } from '../../types/rest/ISortQuery';
import { IUnrestrictedInternalTransfersQuery } from '../../types/accounting/query/IUnrestrictedInternalTransfersQuery';
import { eInternalTransferProcessingStatus } from '../../types/accounting/response/eInternalTransferProcessingStatus';
import { getExpectedInternalTransfersAction } from '../../store/accounting/slices/expectedInternalTransfersSlice';
import { UnrestrictedInternalTransfersSearchTable } from '../../components/accounting/UnrestrictedInternalTransfersSearchTable';
import { createDateRangeFromDays } from '../../utilities/accounting/createDateRangeFromDays';
import { UnrestrictedInternalTransferCreateContainer } from '../../components/accounting/UnrestrictedInternalTransferCreateContainer';
import { createInternalTransferSlice } from '../../store/accounting/slices/createInternalTransferSlice';
import { RootStore } from '../../store/types';
import { ePermissions } from 'types/administration/userAdministration/ePermissions';
import { PermissionWrapper } from 'components/accessControl/PermissionWrapper';
import { PermissionNotification } from '../../components/accessControl/PermissionNotification';
import { useHasPermissions } from '../../components/accessControl/hooks/useHasPermissions';
import { Grid } from '@zillow/constellation';
import { Flex } from '@zillow/constellation';
import { useAppDispatch } from '../../store/store';

const SIZE_PER_PAGE = 25;

const convertLocationSearchToQueryStringParams = (search: string): QueryString.ParsedQs =>
  QueryString.parse(search, { ignoreQueryPrefix: true });

const convertQueryStringParamsToQuery = (
  queryStringParams: QueryString.ParsedQs
): Partial<IUnrestrictedInternalTransfersQuery> => {
  const { createdAt, amountCents, memo, fileNumber, processingStatus, sourceAccount, destinationAccount } =
    queryStringParams;
  const [start, end] = Array.isArray(createdAt) ? createdAt : [];
  return {
    createdAt: typeof start === 'string' && typeof end === 'string' ? [start, end] : undefined,
    amountCents: typeof amountCents === 'string' && !isNaN(parseInt(amountCents)) ? parseInt(amountCents) : undefined,
    memo: typeof memo === 'string' ? memo : undefined,
    fileNumber: typeof fileNumber === 'string' ? fileNumber : undefined,
    processingStatus:
      typeof processingStatus === 'string' && isValidProcessingStatus(processingStatus)
        ? eInternalTransferProcessingStatus[processingStatus]
        : undefined,
    sourceAccount: typeof sourceAccount === 'string' ? sourceAccount : undefined,
    destinationAccount: typeof destinationAccount === 'string' ? destinationAccount : undefined,
    ...convertPaginationQueryStringToPaginationQuery(queryStringParams),
    ...convertSortQueryStringToSortQuery(queryStringParams),
  };
};

const applyDefaultQuery = ({
  createdAt,
  memo,
  pageNumber,
  pageSize,
  unrestricted,
  ...rest
}: Partial<IUnrestrictedInternalTransfersQuery>): IUnrestrictedInternalTransfersQuery => ({
  createdAt: createdAt !== undefined ? createdAt : createDateRangeFromDays(-60),
  memo: memo,
  pageNumber: pageNumber || 1,
  pageSize: pageSize || SIZE_PER_PAGE,
  unrestricted: typeof unrestricted === 'boolean' ? unrestricted : true,
  ...rest,
});

const pushQueryToHistory = <T extends object = {}>(history: any, query: T) => {
  history.push(`?${QueryString.stringify(query, { arrayFormat: 'indices', format: 'RFC3986' })}`);
};

const initializeQueryState = compose(
  applyDefaultQuery,
  convertQueryStringParamsToQuery,
  convertLocationSearchToQueryStringParams
);

function isValidProcessingStatus(status: string): status is keyof typeof eInternalTransferProcessingStatus {
  return Object.keys(eInternalTransferProcessingStatus).includes(status);
}

const resolveProcessingStatus = (status: string) => {
  return isValidProcessingStatus(status) ? eInternalTransferProcessingStatus[status] : 'Unknown';
};

export const UnrestrictedInternalTransfersPage: React.FC<RouteComponentProps> = ({ location }) => {
  const hasReadPermission = useHasPermissions([ePermissions.EscrowInternalTransfersRead]);
  const dispatch = useAppDispatch();
  const { expectedInternalTransfers } = useSelector((store: RootStore) => store.accounting);
  const { query, data, fetchStatus, fetchError } = expectedInternalTransfers;
  const history = useHistory();
  const [showingCreateModal, setShowingCreateModal] = useState(false);
  const processingStatusFilterItems = [
    {
      children: 'Uploaded',
      onClick: () =>
        pushQueryToHistory(history, { ...query, processingStatus: eInternalTransferProcessingStatus.UPLOADED }),
    },
    {
      children: 'Sent',
      onClick: () =>
        pushQueryToHistory(history, { ...query, processingStatus: eInternalTransferProcessingStatus.SENT }),
    },
    {
      children: 'Any Status',
      onClick: () => pushQueryToHistory(history, { ...query, processingStatus: undefined }),
    },
  ];

  // FYI: whenever there is an external querystring change update the query state
  useEffect(() => {
    const unlisten = history.listen(({ search }) => {
      if (hasReadPermission) {
        dispatch(getExpectedInternalTransfersAction(initializeQueryState(search)));
      }
    });
    return () => unlisten();
  }, [history, hasReadPermission, dispatch]);

  // Initialize query params on mount only
  useEffect(() => {
    pushQueryToHistory(history, initializeQueryState(location.search));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PermissionWrapper allowedPermissions={[ePermissions.EscrowInternalTransfersRead]}>
      <PermissionNotification allowedPermissions={[ePermissions.EscrowInternalTransfersEdit]} />
      <Grid display="grid" gridTemplateColumns="1fr max-content" gridAutoFlow="column" padding={0} alignItems="start">
        <Card title="Internal Transfers">
          <ErrorMessage data-test-id={'main-error-location'} error={fetchError} />
          <Flex display="flex" justifyContent="space-between" gap={1}>
            <div>
              <Button
                intent={'primary'}
                onClick={() => {
                  setShowingCreateModal(true);
                }}
              >
                New Internal Transfer
              </Button>
            </div>
            <ButtonDropdown intent={'ghost'} isInline items={processingStatusFilterItems}>
              {query.processingStatus ? resolveProcessingStatus(query.processingStatus) : 'Any Status'}
            </ButtonDropdown>
          </Flex>
          <UnrestrictedInternalTransfersSearchTable
            data={data}
            fetchStatus={fetchStatus}
            query={query}
            setQuery={(query) => pushQueryToHistory(history, query)}
          />
        </Card>
      </Grid>
      {showingCreateModal && (
        <UnrestrictedInternalTransferCreateContainer
          onClose={() => setShowingCreateModal(false)}
          onCreate={() => {
            dispatch(getExpectedInternalTransfersAction(initializeQueryState(location.search)));
            dispatch(createInternalTransferSlice.actions.reset(undefined));
          }}
        />
      )}
    </PermissionWrapper>
  );
};
